/**
 * Active-user summary cards - one card per window (24h / 7d / 30d).
 *
 * Each card shows the viewers tier (anyone with >=1 captured request) split
 * by role, with the actions tier (exception status changes / notes from the
 * audit log) as the stronger secondary signal. FP Users are the adoption
 * headline, so their count leads the role breakdown.
 */

import React from 'react'
import { Box, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material'
import {
  ActivitySummaryResponse,
  WINDOW_LABELS,
  WindowSummary,
} from '../../../../services/activityApi'
import { ROLE_ORDER, getRoleDisplayName } from '../../../../utils/roleDisplay'

// FP_USER first (ROLE_ORDER) - it's the adoption headline; staff follow.
const roleBreakdown = (byRole: Record<string, number>): string =>
  ROLE_ORDER.filter((role) => byRole[role])
    .map((role) => `${getRoleDisplayName(role)} ${byRole[role]}`)
    .join(' · ') || 'None'

const WindowCard: React.FC<{ summary: WindowSummary }> = ({ summary }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ py: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography color="textSecondary" variant="caption">
          {WINDOW_LABELS[summary.window] || summary.window}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={summary.actors_total > 0 ? 'success' : 'default'}
          label={`${summary.actors_total} acted`}
          title={`Users who actioned exceptions (status changes, notes): ${roleBreakdown(summary.actors_by_role)}`}
        />
      </Stack>
      <Typography variant="h4" sx={{ my: 0.5 }}>
        {summary.viewers_total}
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          active users
        </Typography>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {roleBreakdown(summary.viewers_by_role)}
      </Typography>
    </CardContent>
  </Card>
)

const ActivitySummaryCards: React.FC<{ summary: ActivitySummaryResponse | null }> = ({
  summary,
}) => {
  if (!summary) return null
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {summary.windows.map((window) => (
        <Grid item xs={12} sm={4} key={window.window}>
          <WindowCard summary={window} />
        </Grid>
      ))}
      <Grid item xs={12}>
        <Box sx={{ mt: -1 }}>
          <Typography variant="caption" color="text.secondary">
            Active = at least one request in the window. "Acted" counts users with
            exception actions (status changes, notes) - the stronger engagement signal.
          </Typography>
        </Box>
      </Grid>
    </Grid>
  )
}

export default ActivitySummaryCards
